import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Search,
  Users,
  Wallet,
  CheckCircle2,
  Clock,
  Ban,
  ShieldCheck,
  ArrowUpRight,
  Filter,
  IndianRupee,
  FileSpreadsheet,
  Mail,
} from "lucide-react";
import {
  PACKAGES,
  SLOT_LABELS,
} from "@/lib/mockStore";
import { adminApi, adminAbstractsApi, readError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tabs,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const money = (n) =>
  n
    ? new Intl.NumberFormat("en-IN", {
        style: "currency",
        currency: "INR",
        maximumFractionDigits: 0,
      }).format(n)
    : "—";

const STATUS_STYLES = {
  unpaid: "bg-gray-100 text-gray-700",
  pending: "bg-amber-50 text-amber-800 ring-1 ring-amber-200",
  verified: "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200",
};

const relativeTime = (iso) => {
  if (!iso) return "—";
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.round(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  return `${days}d ago`;
};

const StatCard = ({ label, value, sub, icon: Icon, tone = "navy", testId }) => (
  <div
    className={cn(
      "relative overflow-hidden rounded-3xl border p-6",
      tone === "navy" && "border-apicon-navy/10 bg-apicon-navy text-white",
      tone === "gold" && "border-apicon-gold/30 bg-apicon-gold/10",
      tone === "green" && "border-emerald-200 bg-emerald-50",
      tone === "amber" && "border-amber-200 bg-amber-50",
    )}
    data-testid={testId}
  >
    <div className="flex items-center justify-between">
      <span
        className={cn(
          "flex h-10 w-10 items-center justify-center rounded-xl",
          tone === "navy" ? "bg-white/10 text-apicon-gold" : "bg-white text-apicon-navy",
        )}
      >
        <Icon className="h-5 w-5" />
      </span>
      <div
        className={cn(
          "text-[10px] font-semibold uppercase tracking-[0.22em]",
          tone === "navy" ? "text-apicon-gold" : "text-apicon-navy/60",
        )}
      >
        {label}
      </div>
    </div>
    <div
      className={cn(
        "mt-6 font-display text-4xl font-bold",
        tone === "navy" ? "text-white" : "text-apicon-navy",
      )}
    >
      {value}
    </div>
    {sub && (
      <div
        className={cn(
          "mt-1 text-xs",
          tone === "navy" ? "text-white/70" : "text-apicon-navy/60",
        )}
      >
        {sub}
      </div>
    )}
  </div>
);

const Admin = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [tab, setTab] = useState("pending"); // pending | all | verified | unpaid
  const [exporting, setExporting] = useState(false);

  const downloadExcel = async (kind, fetcher, label) => {
    setExporting(kind);
    try {
      const blob = await fetcher();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `apicon-${kind}-${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${label} Excel downloaded.`);
    } catch (err) {
      toast.error(readError(err) || `Could not export ${label.toLowerCase()}.`);
    } finally {
      setExporting(false);
    }
  };
  const [resend, setResend] = useState(null);
  const pollResend = async () => {
    const s = await adminAbstractsApi.resendStatus();
    setResend(s);
    if (s.running) setTimeout(pollResend, 3000);
    else if (s.total) toast.success(`Resend finished: ${s.sent} sent, ${s.failed} failed.`);
  };
  useEffect(() => {
    adminAbstractsApi.resendStatus().then(setResend).catch(() => {});
  }, []);
  const resendAbstracts = async () => {
    try {
      const { queued } = await adminAbstractsApi.resendAll();
      if (!queued) {
        toast.success("All submitted abstracts have already been emailed.");
        return;
      }
      toast(`Sending ${queued} abstracts to the PG inbox…`);
      pollResend();
    } catch (err) {
      toast.error(readError(err) || "Could not resend abstracts.");
    }
  };
  const exportAbstracts = () => downloadExcel("abstracts", adminAbstractsApi.exportExcel, "Abstracts");
  const exportRegistrations = () => downloadExcel("registrations", adminApi.exportRegistrations, "Registrations");

  const load = async () => {
    try {
      const list = await adminApi.listUsers();
      setRows(
        list
          .map((u) => {
            const pkg = PACKAGES.find((p) => p.id === u.packageId);
            return { ...u, packageLabel: pkg?.label || "—" };
          })
          .sort((a, b) => {
            const t = (x) => new Date(x.paymentSubmittedAt || x.createdAt).getTime();
            return t(b) - t(a);
          }),
      );
    } catch (err) {
      toast.error(readError(err) || "Failed to load users.");
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    let list = rows;
    if (tab !== "all") list = list.filter((r) => r.paymentStatus === tab);
    if (q) {
      list = list.filter(
        (r) =>
          r.name.toLowerCase().includes(q) ||
          r.email.toLowerCase().includes(q) ||
          (r.txnRef || "").toLowerCase().includes(q) ||
          (r.phone || "").toLowerCase().includes(q),
      );
    }
    return list;
  }, [rows, query, tab]);

  const stats = useMemo(() => {
    const total = rows.length;
    const pending = rows.filter((r) => r.paymentStatus === "pending").length;
    const verified = rows.filter((r) => r.paymentStatus === "verified");
    const revenue = verified.reduce((s, r) => s + (r.amount || 0), 0);
    return { total, pending, verified: verified.length, revenue };
  }, [rows]);

  const applyStatus = async (id, status, successMsg) => {
    try {
      await adminApi.setPaymentStatus(id, status);
      toast.success(successMsg);
      load();
    } catch (err) {
      toast.error(readError(err) || "Update failed.");
    }
  };

  const verify = (id) => applyStatus(id, "verified", "Payment verified.");
  const reject = (id) => applyStatus(id, "unpaid", "Payment rejected — user marked as unpaid.");
  const revert = (id) => applyStatus(id, "pending", "Reverted to pending.");

  const counts = {
    pending: rows.filter((r) => r.paymentStatus === "pending").length,
    verified: rows.filter((r) => r.paymentStatus === "verified").length,
    unpaid: rows.filter((r) => r.paymentStatus === "unpaid").length,
    all: rows.length,
  };

  return (
    <div className="relative bg-white" data-testid="page-admin">
      {/* Header */}
      <section className="relative overflow-hidden bg-apicon-navy py-14 md:py-16">
        <div className="pointer-events-none absolute inset-0 opacity-[0.08] heritage-pattern" />
        <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-apicon-gold/20 blur-3xl" />

        <div className="relative mx-auto flex w-full max-w-7xl flex-col items-start justify-between gap-6 px-5 md:flex-row md:items-end md:px-8">
          <div>
            <div className="text-xs font-semibold uppercase tracking-[0.22em] text-apicon-gold">
              Admin console
            </div>
            <h1 className="mt-2 font-display text-4xl font-bold tracking-tight text-white sm:text-5xl">
              Registrations &amp; payments
            </h1>
            <p className="mt-3 max-w-xl text-sm text-white/70">
              Review delegate submissions, verify UPI references and keep the
              registration pipeline humming. Signed in as {" "}
              <span className="font-medium text-white">{user?.email}</span>.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button
              onClick={exportRegistrations}
              disabled={!!exporting}
              className="rounded-full bg-apicon-gold text-apicon-navy hover:bg-apicon-gold/90"
              data-testid="admin-export-registrations"
            >
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              {exporting === "registrations" ? "Preparing…" : "Export registrations (Excel)"}
            </Button>
            <Button
              variant="outline"
              onClick={resendAbstracts}
              disabled={resend?.running}
              className="rounded-full border-white/25 bg-white/10 text-white hover:bg-white/20 hover:text-white"
              data-testid="admin-resend-abstracts"
            >
              <Mail className="mr-2 h-4 w-4" />
              {resend?.running
                ? `Sending ${resend.sent + resend.failed}/${resend.total}…`
                : `Resend abstracts to inbox${resend?.notEmailed ? ` (${resend.notEmailed})` : ""}`}
            </Button>
            <Button
              variant="outline"
              onClick={exportAbstracts}
              disabled={!!exporting}
              className="rounded-full border-white/25 bg-white/10 text-white hover:bg-white/20 hover:text-white"
              data-testid="admin-export-abstracts"
            >
              <FileSpreadsheet className="mr-2 h-4 w-4" />
              {exporting === "abstracts" ? "Preparing…" : "Export abstracts (Excel)"}
            </Button>
            <Link to="/admin/content" data-testid="admin-content-link">
              <Button
                variant="outline"
                className="rounded-full border-white/25 bg-white/10 text-white hover:bg-white/20 hover:text-white"
              >
                Committee &amp; Gallery
              </Button>
            </Link>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 py-2 text-xs font-medium text-white">
              <ShieldCheck className="h-4 w-4 text-apicon-gold" />
              Admin access
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="mx-auto -mt-8 w-full max-w-7xl px-5 md:px-8">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4" data-testid="admin-stats">
          <StatCard
            label="Total delegates"
            value={stats.total}
            sub="excluding admin"
            icon={Users}
            tone="navy"
            testId="admin-stat-total"
          />
          <StatCard
            label="Pending"
            value={stats.pending}
            sub="need verification"
            icon={Clock}
            tone="amber"
            testId="admin-stat-pending"
          />
          <StatCard
            label="Verified"
            value={stats.verified}
            sub="paid & confirmed"
            icon={CheckCircle2}
            tone="green"
            testId="admin-stat-verified"
          />
          <StatCard
            label="Revenue"
            value={money(stats.revenue)}
            sub="from verified payments"
            icon={IndianRupee}
            tone="gold"
            testId="admin-stat-revenue"
          />
        </div>
      </section>

      {/* Table */}
      <section className="mx-auto mt-10 w-full max-w-7xl px-5 pb-20 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="overflow-hidden rounded-3xl border border-gray-100 bg-white shadow-sm"
        >
          <div className="flex flex-col gap-4 border-b border-gray-100 p-5 md:flex-row md:items-center md:justify-between">
            <Tabs value={tab} onValueChange={setTab} data-testid="admin-tabs">
              <TabsList className="rounded-full bg-apicon-mist p-1">
                <TabsTrigger
                  value="pending"
                  className="rounded-full data-[state=active]:bg-apicon-navy data-[state=active]:text-white"
                  data-testid="admin-tab-pending"
                >
                  Pending
                  <Badge className="ml-2 rounded-full bg-white/20 text-[10px] text-inherit">
                    {counts.pending}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger
                  value="verified"
                  className="rounded-full data-[state=active]:bg-apicon-navy data-[state=active]:text-white"
                  data-testid="admin-tab-verified"
                >
                  Verified
                  <Badge className="ml-2 rounded-full bg-white/20 text-[10px] text-inherit">
                    {counts.verified}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger
                  value="unpaid"
                  className="rounded-full data-[state=active]:bg-apicon-navy data-[state=active]:text-white"
                  data-testid="admin-tab-unpaid"
                >
                  Unpaid
                  <Badge className="ml-2 rounded-full bg-white/20 text-[10px] text-inherit">
                    {counts.unpaid}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger
                  value="all"
                  className="rounded-full data-[state=active]:bg-apicon-navy data-[state=active]:text-white"
                  data-testid="admin-tab-all"
                >
                  All
                  <Badge className="ml-2 rounded-full bg-white/20 text-[10px] text-inherit">
                    {counts.all}
                  </Badge>
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <div className="relative w-full md:w-72">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Name, email, phone or UTR…"
                className="h-11 rounded-full border-gray-200 pl-10 text-sm focus-visible:ring-apicon-navy"
                data-testid="admin-search"
              />
            </div>
          </div>

          <div className="overflow-x-auto" data-testid="admin-table">
            <Table>
              <TableHeader>
                <TableRow className="bg-apicon-mist/40 hover:bg-apicon-mist/40">
                  <TableHead className="text-xs uppercase tracking-[0.16em] text-apicon-navy">
                    Delegate
                  </TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.16em] text-apicon-navy">
                    Package
                  </TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.16em] text-apicon-navy">
                    Amount
                  </TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.16em] text-apicon-navy">
                    UTR
                  </TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.16em] text-apicon-navy">
                    Submitted
                  </TableHead>
                  <TableHead className="text-xs uppercase tracking-[0.16em] text-apicon-navy">
                    Status
                  </TableHead>
                  <TableHead className="text-right text-xs uppercase tracking-[0.16em] text-apicon-navy">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 && (
                  <TableRow>
                    <TableCell
                      colSpan={7}
                      className="py-12 text-center text-sm text-gray-500"
                      data-testid="admin-empty"
                    >
                      <Filter className="mx-auto mb-3 h-5 w-5 text-gray-400" />
                      No delegates match this view.
                    </TableCell>
                  </TableRow>
                )}
                {filtered.map((r) => (
                  <TableRow
                    key={r.id}
                    className="hover:bg-gray-50"
                    data-testid={`admin-row-${r.id}`}
                  >
                    <TableCell className="max-w-[220px]">
                      <div className="font-medium text-apicon-navy">
                        {r.name}
                      </div>
                      <div className="truncate text-xs text-gray-500">
                        {r.email}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="text-sm text-apicon-navy">
                        {r.packageLabel}
                      </div>
                      <div className="text-xs uppercase tracking-[0.14em] text-gray-500">
                        {SLOT_LABELS[r.slot] || r.slot || "—"}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm font-medium text-apicon-navy">
                      {money(r.amount)}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-gray-700">
                      {r.txnRef || "—"}
                    </TableCell>
                    <TableCell className="text-xs text-gray-500">
                      {relativeTime(r.paymentSubmittedAt || r.createdAt)}
                    </TableCell>
                    <TableCell>
                      <span
                        className={cn(
                          "inline-flex rounded-full px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.16em]",
                          STATUS_STYLES[r.paymentStatus] || STATUS_STYLES.unpaid,
                        )}
                      >
                        {r.paymentStatus}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        {r.paymentStatus === "pending" && (
                          <>
                            <Button
                              size="sm"
                              className="h-8 rounded-full bg-emerald-600 px-3 text-xs font-semibold text-white hover:bg-emerald-700"
                              onClick={() => verify(r.id)}
                              data-testid={`admin-verify-${r.id}`}
                            >
                              <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
                              Verify
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-8 rounded-full border-red-200 px-3 text-xs font-medium text-red-700 hover:bg-red-50"
                              onClick={() => reject(r.id)}
                              data-testid={`admin-reject-${r.id}`}
                            >
                              <Ban className="mr-1 h-3.5 w-3.5" />
                              Reject
                            </Button>
                          </>
                        )}
                        {r.paymentStatus === "verified" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-8 rounded-full text-xs text-gray-500 hover:bg-gray-100"
                            onClick={() => revert(r.id)}
                            data-testid={`admin-revert-${r.id}`}
                          >
                            Revert to pending
                          </Button>
                        )}
                        {r.paymentStatus === "unpaid" && (
                          <span className="inline-flex items-center gap-1 text-xs text-gray-400">
                            Awaiting UTR
                            <ArrowUpRight className="h-3.5 w-3.5" />
                          </span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default Admin;
